'use client';
import { useMediaQuery } from 'usehooks-ts';
import { useState } from 'react';
import InspirationCard, {
  InspirationCardSize,
} from '@/components/inspiration/InspirationCard';
import InspirationHero from '@/components/inspiration/InspirationHero';
import { twMerge } from 'tailwind-merge';
import clsx from 'clsx';
import { Button, DotIcon, GridDotIcon } from '@fagmobler/ui';
import Link from 'next/link';
import { fagmoblerSlugify } from '@/lib/utils/getUrls';
import Image from 'next/image';
import { SimpleMaterializedPage } from '@/app/inspirasjon/page';

type Props = {
  pages: SimpleMaterializedPage[];
};

export function InspirationClientPage({ pages }: Props) {
  const [gridType, setGridType] = useState<'small' | 'large'>('large');
  const isMobile = useMediaQuery('(max-width: 1024px)');

  const largeGridLayout: InspirationCardSize[] = [
    'md',
    'sm',
    'sm',
    'sm',
    'lg',
    'sm',
    'sm',
    'sm',
    'full',
    'sm',
    'md',
    'md',
  ];
  const mobileLargeGridLayout: InspirationCardSize[] = [
    'sm',
    'sm',
    'sm',
    'sm',
    'sm',
    'sm',
    'full',
  ];

  return (
    <>
      <div className="py-6 lg:py-16 lg:border-b border-neutral-50">
        <InspirationHero
          className={twMerge(
            clsx('grid grid-cols-2 lg:grid-cols-3 lg:items-center', {
              'gap-x-4 gap-y-6': isMobile,
              'gap-x-4 gap-y-8': gridType === 'small' && !isMobile,
              'gap-x-4 gap-y-12': gridType === 'large' && !isMobile,
            })
          )}
          title="Inspirasjon til hjemmet"
          description="På inspirasjonssidene kan du finne et vell av kreative ideer og smarte løsninger for innredning som vil forvandle ethvert rom til et stilfullt og funksjonelt hjemmemiljø."
        />
      </div>
      <div className="hidden lg:flex flex-row gap-6 justify-end">
        <Button
          size="sm"
          variant="link"
          onClick={() => setGridType('large')}
          className={twMerge(
            clsx('no-underline hover:opacity-100', {
              'opacity-50': gridType !== 'large',
            })
          )}
          Icon={DotIcon}
          iconPlacement="left"
        >
          Stor grid
        </Button>
        <Button
          size="sm"
          variant="link"
          onClick={() => setGridType('small')}
          className={twMerge(
            clsx('no-underline hover:opacity-100', {
              'opacity-50': gridType !== 'small',
            })
          )}
          Icon={GridDotIcon}
          iconPlacement="left"
        >
          Liten grid
        </Button>
      </div>
      <div
        className={twMerge(
          clsx('grid grid-cols-2 lg:grid-cols-3', {
            'gap-x-4 gap-y-6': isMobile,
            'gap-x-4 gap-y-8': gridType === 'small' && !isMobile,
            'gap-x-4 gap-y-12': gridType === 'large' && !isMobile,
          })
        )}
      >
        {pages.map((page, index) => {
          if (!page.slug?.current || !page.mainImage) {
            return null;
          }

          const size: InspirationCardSize = isMobile
            ? mobileLargeGridLayout[index % mobileLargeGridLayout.length]
            : gridType === 'small'
            ? 'sm'
            : largeGridLayout[index % largeGridLayout.length];

          return (
            <Link
              key={page._id}
              href={`/inspirasjon/${fagmoblerSlugify(page.slug.current)}`}
              className={twMerge(
                clsx({
                  'col-span-2 lg:col-span-3': size === 'full',
                  'lg:col-span-2': size === 'lg',
                })
              )}
            >
              <InspirationCard
                key={page._id + size}
                size={size}
                title={page.title}
                createdAt={page._createdAt}
                tags={page.tags?.map((tag) => tag.title) || []}
                image={
                  page.mainImage.croppedImageUrl && (
                    <Image
                      src={page.mainImage.croppedImageUrl}
                      alt={page.mainImage?.alt || page.title || ''}
                      fill={true}
                      sizes="(max-width: 640px) 100vw, 50vw"
                      placeholder={page.mainImage?.lqip ? 'blur' : undefined}
                      blurDataURL={page.mainImage?.lqip || undefined}
                    />
                  )
                }
              />
            </Link>
          );
        })}
      </div>
    </>
  );
}
